import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { File } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ExportType, ExportFilters, canStartExport, getRemainingCooldownTime } from "@/hooks/useExport";
import { ExportModal } from "@/components/shared/ExportModal";
import { usePermissions } from "@/auth/PermissionsContext";

const EXPORT_PERMISSION_MAP: Record<ExportType, string> = {
  orders: 'metrics.view_sale_data',
  subscriptions: 'metrics.view_sale_data',
  financial: 'financial.view_account_statement',
  'abandoned-carts': 'reports.view_cart_abandonment',
  'physical-orders': 'orders.view_orders_list',
};

interface ExportButtonProps {
  type: ExportType;
  filters?: ExportFilters;
  label?: string;
  className?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  disabled?: boolean;
  title?: string;
}

export function ExportButton({
  type,
  filters = {},
  label,
  className,
  variant = "secondary",
  size = "default",
  disabled = false,
  title,
}: ExportButtonProps) {
  const { t } = useTranslation(["sales", "common"]);
  const { hasPermission } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const requiredPermission = EXPORT_PERMISSION_MAP[type];
  const hasExportPermission = !requiredPermission || hasPermission(requiredPermission);

  React.useEffect(() => {
    const updateTimer = () => {
      if (canStartExport(type)) {
        setRemainingTime(0);
      } else {
        const remaining = getRemainingCooldownTime(type);
        setRemainingTime(remaining);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [type]);

  if (!hasExportPermission) {
    return null;
  }

  const handleClick = () => {
    if (canStartExport(type)) {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const isDisabled = disabled || !canStartExport(type);

  return (
    <>
      <Button
        onClick={handleClick}
        variant={variant}
        size={size}
        disabled={isDisabled}
        className={cn(className)}
      >
        <File className="mr-2 h-4 w-4" />
        {label || t('sales:export')}
        {remainingTime > 0 && ` (${remainingTime}s)`}
      </Button>

      <ExportModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        type={type}
        filters={filters}
        title={title}
      />
    </>
  );
}
